import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    AboutProfessional,
    ReadMoreText,
    TileCarousel,
    type DetailTile,
    detailCta,
    stripHtml,
} from '~/ui-rocco/pages/detail-shared';
import { isProductSupplierProfessional } from '~/modules/professional/util/is-product-supplier-professional';
import { useProjectAccordionRows } from '../hooks/use-project-accordion-rows';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useProjectEnquiryModal } from '../hooks/use-project-enquiry-modal';

/**
 * P8 — About the Supplier / Professional. Maps the project's professional +
 * categories into the shared {@link AboutProfessional} block (dark theme). The
 * header carries a leading hairline + a "(NN — …)" eyebrow that continues the
 * accordion sequence (next item after its rows), and the label reflects whether
 * the professional sells products ("About the Supplier") or is a service
 * professional ("About the Professional").
 *
 * On mobile it renders the product PDP's About-the-Supplier layout so the two
 * pages read the same: the 24px name, category pills beside a clamped summary,
 * the professional's work as a full-bleed peek carousel, then a stacked View
 * Profile / Enquire pair. The standalone {@link RelatedProjectsSection}
 * carousel is the desktop counterpart and hides on mobile (its tiles live here).
 */
export const AboutProfessionalSection = () => {
    const { project } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const professional = project.Professional;
    const { rows } = useProjectAccordionRows();
    const { openEnquiryModal } = useProjectEnquiryModal();
    // Enquire respects the professional's enquiry audience, like every
    // other enquiry CTA on the detail pages.
    const { showEnquiryCTA } = useEnquiryVisibility(professional);

    const categories: CategoryPill[] = (professional.Categories ?? [])
        .slice(0, 5)
        .map((category) => ({ label: category.PrettyTitle ?? '' }))
        .filter((pill) => pill.label !== '');
    const summary = professional.BlockSummary
        ? stripHtml(professional.BlockSummary)
        : undefined;

    const isSupplier = isProductSupplierProfessional({
        hasProducts: professional.HasProducts,
    });
    const number = String(rows.length + 1).padStart(2, '0');
    const eyebrow = `(${number} — About the ${isSupplier ? 'Supplier' : 'Professional'})`;

    if (mobile) {
        return <AboutProfessionalMobile eyebrow={eyebrow} />;
    }

    // No Magic Island here: the block is nearly a screen tall, so a "Read more
    // about" link would hold the island across it and keep the featured-projects
    // pagination below from showing. The block's own View Profile CTA still links
    // out. (Mirrors the product page's About-the-Supplier row.)
    return (
        <AboutProfessional
            theme="dark"
            // Same block as the product page's About the Supplier: name at the
            // title's scale, no pills, description left with the actions right.
            layout="summary"
            hideLogoOnDesktop
            eyebrow={eyebrow}
            name={professional.Title}
            profileLink={professional.Link}
            logo={
                professional.CompanyLogo
                    ? {
                          src: professional.CompanyLogo,
                          background: professional.LogoBackgroundColor,
                      }
                    : undefined
            }
            categories={categories}
            summary={summary}
            secondaryAction={
                showEnquiryCTA
                    ? {
                          label: 'Enquire',
                          onClick: () =>
                              openEnquiryModal({
                                  title: 'Enquire',
                                  ctaLabel: 'Enquire',
                              }),
                      }
                    : undefined
            }
        />
    );
};

/**
 * Mobile About-the-Professional block — the dark-theme twin of the product PDP's
 * `AboutSupplierRowContent` mobile branch: hairline + eyebrow, the 24px name,
 * pills beside a clamped summary, the professional's work as a full-bleed peek
 * carousel, then stacked View Profile / Enquire pills.
 */
const AboutProfessionalMobile = ({ eyebrow }: { eyebrow: string }) => {
    const { project, featuredProjects } = useLoaderData<typeof loader>();
    const professional = project.Professional;
    const { openEnquiryModal } = useProjectEnquiryModal();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);

    const categories: CategoryPill[] = (professional.Categories ?? [])
        .slice(0, 5)
        .map((category) => ({ label: category.PrettyTitle ?? '' }))
        .filter((pill) => pill.label !== '');
    const summary = professional.BlockSummary
        ? stripHtml(professional.BlockSummary)
        : undefined;

    const tiles: DetailTile[] = (
        featuredProjects?.FeaturedProjects?.projects ?? []
    ).map((tile) => ({
        id: tile.id,
        link: tile.link,
        title: tile.title,
        images: tile.images,
        subtitle: tile.professional?.name,
        professional: {
            id: tile.professional?.id,
            name: tile.professional?.name,
        },
        professionalLink: tile.professional?.link,
        pinType: 'ProjectPin' as const,
    }));

    return (
        <section className="bg-transparent px-6 text-white">
            <div className="h-px w-full bg-white/20" />
            <SectionLabel className="mt-3 block text-[0.875rem] tracking-normal text-white">
                {eyebrow}
            </SectionLabel>
            <h2 className="m-0 mt-6 font-sans text-[1.5rem] leading-[1.05] font-normal text-white">
                <RoccoLink to={professional.Link}>
                    {professional.Title}
                </RoccoLink>
            </h2>
            <div className="mt-6 flex items-start gap-6">
                {categories.length > 0 ? (
                    <CategoryPills
                        label="Popular categories:"
                        items={categories}
                        className="w-[9.1875rem] shrink-0 text-white"
                    />
                ) : null}
                {summary ? (
                    <div className="min-w-0 flex-1">
                        <ReadMoreText
                            text={summary}
                            lines={3}
                            anchorHref={professional.Link}
                            className="font-sans text-[0.8125rem] leading-snug text-[#d4d4d4]"
                            toggleClassName="mt-1 text-[0.8125rem] text-white"
                        />
                    </div>
                ) : null}
            </div>
            {tiles.length > 0 ? (
                /* Full-bleed: -mx-6 cancels the section's px-6 gutter; the peek
                   viewport re-insets by 1rem to match the hero gallery. */
                <div className="-mx-6 mt-8">
                    <TileCarousel
                        eyebrow=""
                        ariaLabel={`More from ${professional.Title}`}
                        tiles={tiles}
                        theme="dark"
                        hideHeader
                        hidePagination
                        peek
                    />
                </div>
            ) : null}
            <div className="mt-8 flex flex-col gap-3">
                <RoccoLink
                    to={professional.Link}
                    className={detailCta(
                        'filled-dark',
                        'h-[3.125rem] w-full text-sm',
                    )}
                >
                    View Profile
                </RoccoLink>
                {showEnquiryCTA ? (
                    <button
                        type="button"
                        onClick={() =>
                            openEnquiryModal({
                                title: 'Enquire',
                                ctaLabel: 'Enquire',
                            })
                        }
                        className={detailCta(
                            'outlined-dark',
                            'h-[3.125rem] w-full text-sm',
                        )}
                    >
                        Enquire
                    </button>
                ) : null}
            </div>
        </section>
    );
};
